import { useState, useMemo } from 'react';
import { Copy, Check, ShieldAlert, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';

/**
 * The pump dead-man switch, as a script somebody in a shed can copy and paste.
 *
 * WHY THIS EXISTS
 *   Every fault an irrigation bridge can have costs water, except one. A master
 *   pump that is still running when the bridge believes it stopped will be shut
 *   in against closed valves, and that is a burst main rather than a dry block.
 *
 *   The bridge already does everything it can about that: it retries the OFF
 *   until the node acknowledges it, holds the valves that pump feeds OPEN in the
 *   meantime so it is not shut in, cycles the program so the water is spread
 *   rather than dumped on one block, and raises PUMP_STOP_UNCONFIRMED until
 *   somebody deals with it.
 *
 *   All of that is the bridge acting over the radio it has just been shown it
 *   cannot use. If the pump node is not answering, the bridge cannot switch it
 *   off - it can only make the consequences survivable. So the last decision has
 *   to live on the node, next to the relay.
 *
 *   This script is that. The bridge sends a keepalive while a pump is SUPPOSED
 *   to be running; if the keepalive stops for longer than the timeout, the node
 *   drops its own relay. Which inverts what a broken link means: an OFF command
 *   has to be RECEIVED, a keepalive only has to be MISSED, and only one of those
 *   still works when the radio is the thing that failed.
 *
 * WHY IT IS SHOWN RATHER THAN PUSHED
 *   There is no way to load MicroPython onto an XBee over the mesh. It goes on
 *   over the node's own serial port with XCTU, which means a laptop at the pump
 *   shed either way. Given somebody has to be standing there, the useful thing
 *   for this page to do is hand them the exact text with the right relay and the
 *   right timeout already in it, rather than have them transcribe it.
 *
 * WHY IT ONLY EVER TURNS THE PUMP OFF
 *   The script never switches a relay ON, and takes no part in normal running -
 *   the bridge drives the pump with remote AT commands exactly as it always has.
 *   That is deliberate and it is the most important line in the file. A script
 *   that owned the relay could strand it: a syntax error, an exhausted heap, a
 *   firmware update that wipes the filesystem, and the pump either will not
 *   start or will not stop. A script that can only force the pin LOW has exactly
 *   one failure mode - it does nothing - and "does nothing" is the behaviour
 *   every site has today.
 */

/** XBee 3 only. Nothing older can run MicroPython at all. */
const MIN_TIMEOUT_MIN = 2;
const MAX_TIMEOUT_MIN = 120;

function maskOf(relays: number[]): number {
  return relays.reduce((m, r) => m | (1 << r), 0);
}

function binary8(mask: number): string {
  return '0b' + mask.toString(2).padStart(8, '0');
}

function buildScript(relays: number[], timeoutMin: number): string {
  const mask = maskOf(relays);
  const names = relays.length
    ? relays.map(r => `D${r}`).join(', ')
    : 'NONE SELECTED';
  return `# Factree pump dead-man switch - XBee 3 MicroPython
#
# Guarded relay(s): ${names}
# Timeout: ${timeoutMin} minute(s)
#
# Drops this node's pump relay if the bridge stops saying "still running".
# It NEVER switches the pump ON - the bridge does that with a remote AT
# command exactly as it always has. If this script stops running for any
# reason, the node simply behaves as it did before the script was loaded.

import time
import xbee

TIMEOUT_MS = ${timeoutMin * 60000}          # ${timeoutMin} minute(s)
GUARDED    = ${binary8(mask)}      # ${names}

last_seen = time.ticks_ms()
armed = 0                     # what the last keepalive said may stay on

while True:
    packet = xbee.receive()
    if packet:
        payload = packet.get('payload') or b''
        if payload[:2] == b'KA' and len(payload) >= 3:
            last_seen = time.ticks_ms()
            armed = payload[2] & GUARDED

    if time.ticks_diff(time.ticks_ms(), last_seen) > TIMEOUT_MS:
        # Expired. Force every guarded relay the last keepalive did not name
        # LOW, on EVERY pass rather than once on the edge - a duplicate ON
        # arriving from a retrying coordinator after the expiry must not be
        # able to leave the pump running until the next timeout.
        for index in range(8):
            bit = 1 << index
            if (GUARDED & bit) and not (armed & bit):
                try:
                    xbee.atcmd('D%d' % index, 4)
                except Exception:
                    pass
        armed = 0

    time.sleep_ms(200)
`;
}

export default function PumpDeadmanScript() {
  const [open, setOpen] = useState(false);
  const [relays, setRelays] = useState<number[]>([3]);
  const [timeoutMin, setTimeoutMin] = useState(10);
  const [copied, setCopied] = useState(false);

  const script = useMemo(
    () => buildScript([...relays].sort((a, b) => a - b), timeoutMin),
    [relays, timeoutMin]
  );

  function toggleRelay(index: number) {
    setRelays(prev =>
      prev.includes(index) ? prev.filter(r => r !== index) : [...prev, index]
    );
  }

  async function copyScript() {
    try {
      await navigator.clipboard.writeText(script);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard blocked (insecure origin, or a locked-down browser). The
         script is selectable in the box below, so this is not worth an error
         banner - it is worth the button simply not confirming. */
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <button
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-2 text-left"
          >
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <ShieldAlert className="w-4 h-4 text-amber-600" />
            <span>Pump safety script (XBee 3)</span>
          </button>
        </CardTitle>
      </CardHeader>

      {open && (
        <CardContent className="pt-0 space-y-4">
          <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">What this does</p>
              <p className="mt-1">
                The bridge sends a keepalive to a pump node while that pump is
                meant to be running. With this script loaded, the node switches
                its own pump off if the keepalive stops for longer than the
                timeout — so a pump whose radio has died stops by itself instead
                of running until somebody drives out to it.
              </p>
              <p className="mt-2">
                It only ever switches the pump <strong>off</strong>. Loading it
                changes nothing about how the pump is normally controlled, and a
                node without it behaves exactly as it does today.
              </p>
            </div>
          </div>

          {/* Configuration. The two things that differ per pump, and the two
              things somebody standing at the shed cannot be expected to edit
              correctly in a text box on a phone. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                Pump relay(s) on this node
              </label>
              <div className="flex flex-wrap gap-1.5">
                {[0, 1, 2, 3, 4, 5, 6, 7].map(index => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => toggleRelay(index)}
                    className={
                      relays.includes(index)
                        ? 'px-2.5 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white'
                        : 'px-2.5 py-1.5 text-xs font-medium rounded-lg bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }
                  >
                    D{index}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-neutral-500">
                Only the relays that drive a pump. A zone valve on the same node
                must be left out, or this will close it too.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                Timeout: {timeoutMin} minute{timeoutMin === 1 ? '' : 's'}
              </label>
              <input
                type="range"
                min={MIN_TIMEOUT_MIN}
                max={MAX_TIMEOUT_MIN}
                step={1}
                value={timeoutMin}
                onChange={e => setTimeoutMin(Number(e.target.value))}
                className="w-full"
              />
              <p className="mt-1.5 text-xs text-neutral-500">
                How long the node waits before acting. This is also the worst
                case: a pump can run for this long after the bridge loses the
                ability to reach it. The bridge sends a keepalive every minute,
                so 10 minutes tolerates nine lost in a row before a running pump
                is dropped mid-irrigation.
              </p>
            </div>
          </div>

          {relays.length === 0 && (
            <p className="text-sm text-red-600">
              Select at least one relay — as written this script would guard
              nothing.
            </p>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-neutral-600">
                MicroPython — paste this into XCTU
              </span>
              <Button size="sm" variant="secondary" onClick={copyScript}>
                {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <pre className="p-3 bg-neutral-900 text-neutral-100 rounded-xl text-xs overflow-x-auto max-h-96 whitespace-pre">
              {script}
            </pre>
          </div>

          {/* The procedure. Written as steps rather than prose because it is
              read one-handed, on a phone, next to a running pump. */}
          <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-700">
            <p className="font-medium mb-2">Loading it with XCTU</p>
            <ol className="list-decimal ml-5 space-y-1.5">
              <li>
                Take the pump node's XBee out and put it in a USB adapter, then
                add it in XCTU.
              </li>
              <li>
                Set <code className="px-1 bg-neutral-200 rounded">AP = 4</code>{' '}
                (MicroPython REPL) and write the settings. MicroPython only runs
                in this mode.
              </li>
              <li>
                Open the MicroPython Terminal (under Tools). Press{' '}
                <code className="px-1 bg-neutral-200 rounded">Ctrl+E</code> for
                paste mode, paste the script, then{' '}
                <code className="px-1 bg-neutral-200 rounded">Ctrl+D</code> to
                run it. Paste mode matters — the REPL auto-indents, and pasting
                without it mangles every indented block.
              </li>
              <li>
                To make it permanent, upload the same text as{' '}
                <code className="px-1 bg-neutral-200 rounded">main.py</code>{' '}
                with XCTU's File System Manager, then set{' '}
                <code className="px-1 bg-neutral-200 rounded">PS = 1</code> so it
                runs at boot, and write the settings.
              </li>
              <li>
                Leave <code className="px-1 bg-neutral-200 rounded">AP = 4</code>{' '}
                and put the module back. That setting only governs the node's own
                serial port, which nothing on a pump node uses; commands from the
                bridge arrive over the air and are unaffected.
              </li>
              <li>
                <strong>Prove it on the first one.</strong> With the pump
                running, unplug the coordinator's USB lead and confirm the pump
                stops after the timeout. That physical test is the only evidence
                that counts — do it once per site, not once per node.
              </li>
            </ol>
          </div>

          <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
            <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Before you roll this out</p>
              <ul className="list-disc ml-4 mt-1 space-y-1">
                <li>
                  <strong>XBee 3 only.</strong> S2C and S2B cannot run
                  MicroPython. Check{' '}
                  <code className="px-1 bg-red-100 rounded">HV</code> and{' '}
                  <code className="px-1 bg-red-100 rounded">VR</code> on the node
                  first — if it is not an XBee 3, this script does nothing at all
                  and the node needs a hardware timer relay instead.
                </li>
                <li>
                  Do one site at a time and finish it. A site where some pumps
                  stop themselves and some do not is one nobody can reason about
                  during a fault.
                </li>
                <li>
                  This does not help with a welded contactor, or a pump wired to
                  run without the node. Those are hardware interlocks.
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
